import { useTranslation } from 'react-i18next';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';
import { atomListFromTool } from '@peruselab/structure-editor/domain/markushLayers';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import { Icon } from '../../components/Icon';

interface RGroupToolProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  /** Labels already on the drawing, in first-use order. */
  readonly usedLabels: readonly string[];
  /** The next unused label, offered as "new". */
  readonly newLabel: string;
  /** #654 -- opens the periodic table to pick a Markush atom list. */
  readonly onOpenAtomList?: () => void;
  /** #656 -- brackets the current selection as a repeat unit; `enabled` when it qualifies. */
  readonly bracket?: { readonly enabled: boolean; readonly onBracket: () => void };
  // #674 -- a disclosure in the rail's single-open accordion, owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
}

function armedLabel(tool: EditorTool): string | null {
  return tool.startsWith('r-group-') ? tool.slice('r-group-'.length) : null;
}

/** #674 -- R1-R3 are always offered: most Markush claims stop there. */
const OFFERED_LABELS = ['R1', 'R2', 'R3'] as const;

function labelNumber(label: string): number {
  return Number(/^R(\d+)$/.exec(label)?.[1] ?? 0);
}

/** The chips to show -- R1-R3, every label in use, and the armed one -- plus the label the "+"
 *  chip arms: the next beyond R1-R3 and every label on the drawing (`newLabel` also counts
 *  definitions). An armed-but-unplaced label does not count, so repeated "+" never skips numbers. */
export function rGroupChipLabels(
  usedLabels: readonly string[],
  newLabel: string,
  armed: string | null
): { readonly chips: readonly string[]; readonly next: string } {
  const chips = [...new Set([...OFFERED_LABELS, ...usedLabels, ...(armed ? [armed] : [])])].sort(
    (a, b) => labelNumber(a) - labelNumber(b) || a.localeCompare(b)
  );
  const highest = Math.max(...[...OFFERED_LABELS, ...usedLabels].map(labelNumber));
  return { chips, next: `R${Math.max(labelNumber(newLabel), highest + 1)}` };
}

// #648 (Markush M3a) -- the R-group label tool: the label text is the glyph, the way an element
// symbol is the periodic-table tool's glyph. #674: a row of chips -- R1-R3 always, then any other
// label in use, then "+" for the next one -- with labels already on the drawing marked in use. Arming works like `element-${z}`: pick a label, then click terminal atoms on the paper;
// clicking the armed button again returns to Draw. #674: always available, as the rail's Markush
// disclosure -- using any of these tools on a drawing is what makes it a Markush query.
export function RGroupToolGroup({
  tool,
  setTool,
  usedLabels,
  newLabel,
  onOpenAtomList,
  bracket,
  open,
  onToggle
}: RGroupToolProps) {
  const { t } = useTranslation();
  const armed = armedLabel(tool);
  const { chips, next } = rGroupChipLabels(usedLabels, newLabel, armed);
  return (
    <details id="rail-group-markush" className="workspace-rail-markush" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.groups.markush')}
      </summary>
      <div className="workspace-rail-markush-body" role="group" aria-label={t('workspace.rail.markush.group')}>
        <div className="r-group-chips">
          {chips.map((label) => {
            const isArmed = armed === label;
            const inUse = usedLabels.includes(label);
            const name = t(inUse ? 'workspace.rail.markush.label' : 'workspace.rail.markush.newLabel', { label });
            return (
              <button
                key={label}
                type="button"
                className={`r-group-tool r-group-chip ${inUse ? 'in-use' : ''} ${isArmed ? 'active' : ''}`}
                data-testid={`r-group-tool-${label}`}
                aria-pressed={isArmed}
                aria-label={name}
                title={name}
                onClick={() => setTool(isArmed ? 'draw' : `r-group-${label}`)}
              >
                <span className="r-group-glyph" aria-hidden="true">
                  {label}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className="icon-button r-group-chip"
            data-testid="r-group-tool-next"
            aria-label={t('workspace.rail.markush.newLabel', { label: next })}
            title={t('workspace.rail.markush.newLabel', { label: next })}
            // Already armed: place it first (it shows as its own chip), so "+" can't skip numbers.
            disabled={armed === next}
            onClick={() => setTool(`r-group-${next}`)}
          >
            <Icon name="add" />
          </button>
        </div>
        <div className="r-group-actions">
          {onOpenAtomList ? <AtomListButton tool={tool} onOpen={onOpenAtomList} /> : null}
          {bracket ? <RepeatUnitButton {...bracket} /> : null}
        </div>
      </div>
    </details>
  );
}

/**
 * #654 -- the atom-list tool: opens the periodic table in multi-select mode; its Done arms
 * `atom-list-${z,…}`. The glyph is the list itself once armed (`[C,N,O,S]`), `[ ]` before --
 * text as glyph, like the R labels and the periodic-table tool's element symbol.
 */
export function AtomListButton({ tool, onOpen }: { readonly tool: EditorTool; readonly onOpen: () => void }) {
  const { t } = useTranslation();
  const armed = atomListFromTool(tool);
  const list = armed ? `[${armed.map(elementSymbol).join(',')}]` : null;
  const name = list ? t('workspace.rail.markush.atomListArmed', { list }) : t('workspace.rail.markush.atomList');
  return (
    <button
      type="button"
      className={`button-with-icon r-group-tool ${armed ? 'active' : ''}`}
      data-testid="atom-list-tool"
      aria-pressed={armed !== undefined}
      aria-label={name}
      title={name}
      onClick={onOpen}
    >
      <span className="r-group-glyph" aria-hidden="true">
        {list ?? '[ ]'}
      </span>
    </button>
  );
}

/**
 * #656 -- brackets the selected atoms as a repeat unit (`( )ₙ` glyph). An action, not an armed
 * tool: select atoms first (Select tool, marquee or lasso), then press it; disabled until the
 * selection is a connected run of concrete atoms. The new unit's count pop-up opens at once.
 */
export function RepeatUnitButton({
  enabled,
  onBracket
}: {
  readonly enabled: boolean;
  readonly onBracket: () => void;
}) {
  const { t } = useTranslation();
  const name = t(enabled ? 'workspace.rail.markush.repeatUnit' : 'workspace.rail.markush.repeatUnitHint');
  return (
    <button
      type="button"
      className="button-with-icon r-group-tool"
      data-testid="repeat-unit-tool"
      aria-label={name}
      title={name}
      disabled={!enabled}
      onClick={onBracket}
    >
      <span className="r-group-glyph" aria-hidden="true">
        ( )ₙ
      </span>
    </button>
  );
}
